var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState, useRef } from 'react';
import { Button, DrawerHeader, DrawerBody, DrawerFooter, FormController, Link, Typography, Tile, TileGroup, Alert, Skeleton, SkeletonLine, LoadingButton, } from '@one-thd/sui-atomic-components';
import { RadioButtonUnchecked, ConfirmedFilled } from '@one-thd/sui-icons';
import { usersApi } from '../../../api/b2b/users';
import { sortUsers } from '../../../utils/user';
import { ERR_GENERIC_WITH_CANCEL } from '../../../constants/response-error-messages';
import { CHANGES_SAVED_MSG } from '../../../constants/response-success-messages';
import { getUsersForTenderRequest, updateUsersForTenderRequest, } from '../../../api/b2b/payments';
import { createUpdateUsersForTenderRequestPayload } from '../../../utils/update-permissions';
export const SelectPhaPermissions = ({ hideDrawer, onSuccess, paymentId, }) => {
    const [selectedUsers, setSelectedUsers] = useState([]);
    const initiallySelectedUsers = useRef([]);
    const [allUsers, setAllUsers] = useState([]);
    const [isError, setIsError] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const totalUsers = allUsers.length;
    const handleTileChange = (_event, newFormats) => {
        setSelectedUsers(newFormats);
    };
    const handleSelectAll = () => {
        if (allUsers.length === selectedUsers.length) {
            setSelectedUsers([]);
        }
        else {
            const allUserIds = allUsers.map((user) => user.userId.toString());
            setSelectedUsers(allUserIds);
        }
    };
    const getSelectAllText = () => {
        if (allUsers.length === selectedUsers.length) {
            return `Deselect All (${selectedUsers.length} selected)`;
        }
        if (selectedUsers.length > 0) {
            return `Select All (${selectedUsers.length} selected)`;
        }
        return 'Select All';
    };
    useEffect(() => {
        const fetchUsers = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            const response = yield usersApi.getUsers();
            const users = ((_a = response.data) === null || _a === void 0 ? void 0 : _a.users) || [];
            const filteredUsers = users.filter((user) => user.role.toUpperCase() !== 'RUNNER');
            setAllUsers(sortUsers(filteredUsers));
        });
        const fetchPreExistingUsers = () => __awaiter(void 0, void 0, void 0, function* () {
            const { userIds } = yield getUsersForTenderRequest(paymentId);
            initiallySelectedUsers.current = [...userIds];
            setSelectedUsers([...userIds]);
        });
        setIsError(false);
        setLoadingUsers(true);
        Promise.all([fetchUsers(), fetchPreExistingUsers()])
            .catch(() => {
            handleError();
        })
            .finally(() => {
            setLoadingUsers(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleSuccess = () => {
        onSuccess(CHANGES_SAVED_MSG.message);
        hideDrawer();
    };
    const handleError = () => {
        setIsError(true);
    };
    const handleSaveChanges = () => {
        const request = createUpdateUsersForTenderRequestPayload(selectedUsers, initiallySelectedUsers.current);
        setIsError(false);
        return updateUsersForTenderRequest(paymentId, request)
            .then(({ success }) => {
            if (!success) {
                handleError();
                return;
            }
            handleSuccess();
        })
            .catch(() => {
            handleError();
        });
    };
    const SkeletonLoader = () => {
        return (React.createElement("div", { className: "sui-ml-[-24px]" },
            React.createElement(Skeleton, { className: "sui-grow", "data-testid": "skeleton-loader" },
                React.createElement(SkeletonLine, { numberOfLines: 3 }))));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DrawerHeader, { title: `Users (${totalUsers})`, onClose: () => {
                hideDrawer();
            } }),
        React.createElement(DrawerBody, null,
            React.createElement("div", { className: "sui-flex sui-flex-col sui-justify-between sui-h-full" },
                React.createElement("div", { className: "sui-mb-4" },
                    React.createElement("div", { className: "sui-mb-4" },
                        React.createElement(Link, { component: "button", onClick: () => { } }, "Add New User")),
                    React.createElement("div", { className: "sui-mb-2" },
                        React.createElement(Typography, { variant: "body-base", weight: "bold" }, "Select which users should have permissions to use this line of credit."),
                        React.createElement(Typography, null, "You may grant permissions to Admins and Purchasers on your account to use this line of credit when checking out on HomeDepot.com. The balance of this line of credit will be available to all users you grant permissions to.")),
                    React.createElement("div", { className: "sui-mb-4" },
                        React.createElement(Link, { component: "button", onClick: handleSelectAll, "data-testid": "select-text" }, getSelectAllText())),
                    loadingUsers && React.createElement(SkeletonLoader, null),
                    !loadingUsers && (React.createElement(FormController, { fullWidth: true },
                        React.createElement(TileGroup, { className: "sui-grid sui-grid-cols-1 sui-gap-4", value: selectedUsers, onChange: handleTileChange, "aria-label": "permissions selection", disableExclusive: true, "data-testid": "permissions-tiles" }, allUsers.map((user) => (React.createElement(Tile, { key: user.userId.toString(), value: user.userId.toString(), "aria-label": `Select ${user.firstName} ${user.lastName}`, grow: true },
                            React.createElement("div", { className: "sui-flex sui-flex-col sui-w-full" },
                                React.createElement("div", { className: "sui-flex sui-justify-between sui-items-center sui-w-full" },
                                    React.createElement(Typography, { className: "sui-truncate sui-text-left" }, user.firstName + ' ' + user.lastName),
                                    React.createElement("div", { className: "sui-ml-4 sui-flex sui-justify-end" }, selectedUsers.includes(user.userId.toString()) ? (React.createElement(ConfirmedFilled, { size: "small", color: "primary", "data-testid": "confirmed-checkbox" })) : (React.createElement(RadioButtonUnchecked, { size: "small", "data-testid": "radio-button-unchecked" })))),
                                React.createElement("div", { className: "sui-w-full" },
                                    React.createElement(Typography, { variant: "body-sm", color: "subtle", className: "sui-text-left" }, user.role)))))))))),
                isError && (React.createElement("div", { "data-testid": "select-pha-drawer-alert", className: "sui-mb-[-18px]" },
                    React.createElement(Alert, { status: "error" }, ERR_GENERIC_WITH_CANCEL.message))))),
        React.createElement(DrawerFooter, null,
            React.createElement("div", { className: "sui-flex sui-flex-col sui-gap-4" },
                React.createElement(LoadingButton, { fullWidth: true, loading: isSaveLoading, variant: "primary", onClick: () => {
                        setIsSaveLoading(true);
                        handleSaveChanges().finally(() => {
                            setIsSaveLoading(false);
                        });
                    } }, "Save Changes"),
                React.createElement(Button, { fullWidth: true, variant: "secondary", onClick: () => {
                        setIsError(false);
                        hideDrawer();
                    } }, "Cancel")))));
};
